/**
 * GA4 funnel events — single source of truth for conversion reporting.
 * Funnel: view → first_calculator_input (once/session) → calculation_complete → calculator_destination
 *
 * Phase 0 paywall reporting also uses:
 * - paywall_surface (banner | post_result | pdf | exhausted_gate | homepage | other)
 * - advanced_run_clicked / advanced_run_succeeded (every attempt; not session-deduped)
 */

const FIRST_INPUT_PREFIX = 'scp_ga_first_input';
const CALC_COMPLETE_PREFIX = 'scp_ga_calc_complete';
const TRANSFER_RECEIVED_PREFIX = 'scp_ga_transfer_received';
const BASIC_ADVANCED_CTA_SHOWN_PREFIX = 'scp_ga_basic_adv_cta_shown';
const ADVANCED_DEMO_SHOWN_PREFIX = 'scp_ga_advanced_demo_shown';

/** Coarse buckets for checkout / pricing breakdowns in GA4. */
export const PAYWALL_SURFACE = Object.freeze({
  BANNER: 'banner',
  POST_RESULT: 'post_result',
  PDF: 'pdf',
  EXHAUSTED_GATE: 'exhausted_gate',
  HOMEPAGE: 'homepage',
  OTHER: 'other'
});

/**
 * Map granular CTA `surface` strings to the five Phase 0 buckets.
 * Keep raw `surface` elsewhere; attach this as `paywall_surface`.
 */
export function normalizePaywallSurface(surface = '') {
  const s = String(surface || '').toLowerCase().trim();
  if (!s || s === 'unknown') return PAYWALL_SURFACE.OTHER;

  // Specific first — order matters.
  if (/(^|_)pdf(_|$)|pdf_export|csv_export/.test(s)) return PAYWALL_SURFACE.PDF;
  if (/post_result|post-result|post_session/.test(s)) return PAYWALL_SURFACE.POST_RESULT;
  if (/run_button|email_gate/.test(s)) return PAYWALL_SURFACE.EXHAUSTED_GATE;
  if (
    /free_runs_exhausted|demo_banner|app_top_banner|calculator_banner|(_|^)banner(_|$)/.test(s)
  ) {
    return PAYWALL_SURFACE.BANNER;
  }
  if (
    s === 'homepage' ||
    s.startsWith('homepage') ||
    /article_top|landing_paid|calculator_main_paid|worst_years_article|paid_cta/.test(s)
  ) {
    return PAYWALL_SURFACE.HOMEPAGE;
  }

  return PAYWALL_SURFACE.OTHER;
}

function withPaywallSurface(params = {}) {
  const surface = params.surface ?? '';
  return {
    ...params,
    paywall_surface: params.paywall_surface || normalizePaywallSurface(surface)
  };
}

function getRuntime() {
  if (typeof globalThis !== 'undefined') return globalThis;
  if (typeof window !== 'undefined') return window;
  return null;
}

export function pushFunnelEvent(eventName, params = {}) {
  const root = getRuntime();
  if (!root) return;
  if (typeof root.gtag === 'function') {
    root.gtag('event', eventName, params);
  } else if (Array.isArray(root.dataLayer)) {
    root.dataLayer.push({ event: eventName, ...params });
  }
}

const FUNNEL_EVENT_ACK_TIMEOUT_MS = 2000;

/**
 * Push a funnel event and wait until GA4 acknowledges it (or timeout).
 * Use before window.location redirects so checkout_started is not dropped.
 */
export function pushFunnelEventAsync(eventName, params = {}, timeoutMs = FUNNEL_EVENT_ACK_TIMEOUT_MS) {
  const root = getRuntime();
  if (!root) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;
    const settle = (sent) => {
      if (settled) return;
      settled = true;
      resolve(sent);
    };
    const timer = setTimeout(() => settle(false), timeoutMs);
    const payload = {
      ...params,
      transport_type: 'beacon',
      event_callback: () => {
        clearTimeout(timer);
        settle(true);
      }
    };

    if (typeof root.gtag === 'function') {
      root.gtag('event', eventName, payload);
      return;
    }

    if (Array.isArray(root.dataLayer)) {
      root.dataLayer.push({ event: eventName, ...params });
    }
    clearTimeout(timer);
    settle(false);
  });
}

function getSessionStorage() {
  const root = getRuntime();
  return root?.sessionStorage ?? (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
}

function getPersistentStorage() {
  const root = getRuntime();
  try {
    const ls = root?.localStorage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
    if (ls) {
      ls.setItem('_scp_ga_probe', '1');
      ls.removeItem('_scp_ga_probe');
      return ls;
    }
  } catch {
    /* private mode */
  }
  return getSessionStorage();
}

function markConversionOnce(keyPrefix, transactionId) {
  if (!transactionId) return false;
  const storage = getPersistentStorage();
  const key = `${keyPrefix}_${transactionId}`;
  if (!storage) return true;
  try {
    if (storage.getItem(key)) return false;
    storage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

function markSessionOnce(key) {
  const storage = getSessionStorage();
  if (!storage) return true;
  try {
    if (storage.getItem(key)) return false;
    storage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

/**
 * Step 2 — first meaningful interaction (once per tier + platform per session).
 */
export function trackFirstCalculatorInput({
  calculatorTier = 'basic',
  platform = 'desktop',
  interaction = 'edit'
} = {}) {
  const key = `${FIRST_INPUT_PREFIX}_${calculatorTier}_${platform}`;
  if (!markSessionOnce(key)) return false;

  pushFunnelEvent('first_calculator_input', {
    event_category: 'funnel',
    event_label: interaction,
    value: 1,
    calculator_tier: calculatorTier,
    platform,
    ab_variant: getRuntime()?._abVariant || 'control'
  });
  return true;
}

/**
 * Step 3 — calculation finished and results shown (all tiers).
 * Once per tier+platform per session so funnel rates cannot exceed 100% of
 * prior steps from multi-fire / auto-recalc. Backfills first_input when a
 * calc runs without a prior tracked edit (URL-prefill, example load, etc.).
 */
export function trackCalculationComplete({
  calculatorTier = 'basic',
  calculatorType,
  platform = 'desktop',
  extra = {}
} = {}) {
  trackFirstCalculatorInput({
    calculatorTier,
    platform,
    interaction: 'implicit_via_calculation'
  });

  const key = `${CALC_COMPLETE_PREFIX}_${calculatorTier}_${platform}`;
  if (!markSessionOnce(key)) return false;

  const label = calculatorType || calculatorTier;

  pushFunnelEvent('calculation_complete', {
    event_category: 'conversion',
    event_label: label,
    value: 1,
    calculator_tier: calculatorTier,
    platform,
    ab_variant: getRuntime()?._abVariant || 'control',
    ...extra
  });

  // Legacy name still used in some GA4 explorations
  if (calculatorTier === 'advanced') {
    pushFunnelEvent('calculator_completed', {
      event_category: 'funnel',
      calculator_tier: 'advanced',
      event_label: label,
      ...extra
    });
  }
  return true;
}

/**
 * User navigates from one calculator tier to another (e.g. Basic → Advanced CTA).
 */
export function trackCalculatorDestination({
  source,
  destination,
  eventLabel,
  calculatorTier = 'basic'
} = {}) {
  if (!source || !destination) return;

  pushFunnelEvent('calculator_destination', {
    event_category: 'funnel',
    event_label: eventLabel || `${source}_to_${destination}`,
    source,
    destination,
    calculator_tier: calculatorTier,
    value: 1
  });

  if (source === 'basic' && destination === 'advanced') {
    pushFunnelEvent('basic_results_advanced_cta_click', {
      event_category: 'conversion',
      event_label: eventLabel || 'Basic to Advanced',
      source,
      destination,
      calculator_tier: 'basic',
      value: 1
    });
  }
}

/**
 * Basic result CTA exposure. Lets GA4 compare users who saw the Advanced CTA
 * with those who clicked through.
 */
export function trackBasicAdvancedCtaShown({
  surface = 'unknown',
  platform = 'desktop'
} = {}) {
  const key = `${BASIC_ADVANCED_CTA_SHOWN_PREFIX}_${surface}_${platform}`;
  if (!markSessionOnce(key)) return false;

  pushFunnelEvent('basic_results_advanced_cta_shown', {
    event_category: 'funnel',
    event_label: surface,
    surface,
    platform,
    calculator_tier: 'basic',
    value: 1
  });
  return true;
}

/**
 * Advanced demo exposure. Lets GA4 separate users who reached Advanced from
 * users who actually saw the locked-input demo/value explanation.
 */
export function trackAdvancedDemoShown({
  surface = 'example_selector',
  calculatorTier = 'advanced'
} = {}) {
  const key = `${ADVANCED_DEMO_SHOWN_PREFIX}_${surface}_${calculatorTier}`;
  if (!markSessionOnce(key)) return false;

  pushFunnelEvent('advanced_demo_shown', {
    event_category: 'funnel',
    event_label: surface,
    surface,
    calculator_tier: calculatorTier,
    value: 1
  });
  return true;
}

/**
 * Destination calculator applied transfer payload (once per transfer per session).
 */
export function trackTransferReceived({ source, destination } = {}) {
  if (!source || !destination) return;
  const key = `${TRANSFER_RECEIVED_PREFIX}_${source}_${destination}`;
  if (!markSessionOnce(key)) return;

  pushFunnelEvent('calculator_transfer_received', {
    event_category: 'funnel',
    event_label: `${source}_to_${destination}`,
    source,
    destination,
    calculator_tier: destination,
    value: 1
  });
}

/** Pricing surface shown (modal open or direct checkout intent). */
export function trackPricingOpened({
  source = 'unknown',
  calculatorTier = 'unknown',
  surface = 'pricing_modal',
  pricingView = 'simple'
} = {}) {
  pushFunnelEvent(
    'pricing_opened',
    withPaywallSurface({
      event_category: 'funnel',
      source,
      calculator_tier: calculatorTier,
      surface,
      pricing_view: pricingView,
      value: 1
    })
  );
}

/** User chose a plan in the pricing modal (before Stripe redirect). */
export function trackPricingPlanClick({
  tier,
  billing,
  source = 'unknown',
  calculatorTier = 'unknown',
  view = 'simple',
  surface = 'pricing_modal'
} = {}) {
  pushFunnelEvent(
    'pricing_plan_click',
    withPaywallSurface({
      event_category: 'funnel',
      event_label: `${tier}_${billing}`,
      tier,
      billing_cycle: billing,
      source,
      calculator_tier: calculatorTier,
      pricing_view: view,
      surface,
      value: 1
    })
  );
}

function buildCheckoutStartedParams({
  tier,
  billing,
  source = 'unknown',
  calculatorTier = 'unknown',
  surface = 'unknown',
  pricingView = 'simple'
} = {}) {
  return withPaywallSurface({
    event_category: 'conversion',
    event_label: `${tier}_${billing}`,
    tier,
    billing_cycle: billing,
    source,
    calculator_tier: calculatorTier,
    pricing_view: pricingView,
    surface,
    value: 1
  });
}

/**
 * Advanced Run button pressed (every click that starts a run attempt).
 * Fires even when allocation / validation later aborts — contrast with
 * advanced_run_succeeded. Not session-deduped.
 */
export function trackAdvancedRunClicked({
  platform = 'desktop',
  relationshipStatus,
  hasSubscription,
  freeRunsRemaining,
  extra = {}
} = {}) {
  pushFunnelEvent('advanced_run_clicked', {
    event_category: 'funnel',
    event_label: 'advanced',
    calculator_tier: 'advanced',
    platform,
    relationship_status: relationshipStatus,
    has_subscription: hasSubscription,
    free_runs_remaining: freeRunsRemaining,
    value: 1,
    ...extra
  });
}

/**
 * Advanced run finished and results were shown (every successful run).
 * Distinct from calculation_complete (once per session) and from run_clicked.
 */
export function trackAdvancedRunSucceeded({
  platform = 'desktop',
  relationshipStatus,
  sustainableIncome,
  extra = {}
} = {}) {
  pushFunnelEvent('advanced_run_succeeded', {
    event_category: 'funnel',
    event_label: 'advanced',
    calculator_tier: 'advanced',
    platform,
    relationship_status: relationshipStatus,
    sustainable_income: sustainableIncome,
    value: 1,
    ...extra
  });
}

/** Stripe Checkout session created; await before redirect so GA4 receives the hit. */
export function trackCheckoutStarted(opts = {}) {
  return pushFunnelEventAsync('checkout_started', buildCheckoutStartedParams(opts));
}

const PURCHASE_FIRED_PREFIX = 'scp_ga_purchase_fired';
const RESTORE_FIRED_PREFIX = 'scp_ga_restore_fired';

function buildPurchasePayload({
  transactionId,
  value,
  currency = 'AUD',
  tier,
  billing,
  source = '',
  landingSource = '',
  article = '',
  ctaLocation = '',
  surface = '',
  landingPath = '',
  utmCampaign = '',
  context = ''
}) {
  return withPaywallSurface({
    transaction_id: transactionId,
    value: value ?? 0,
    currency,
    tier: tier || '',
    billing_cycle: billing || '',
    source: source || '',
    landing_source: landingSource || '',
    article: article || '',
    cta_location: ctaLocation || '',
    surface: surface || '',
    landing_path: landingPath || '',
    utm_campaign: utmCampaign || '',
    unlock_context: context,
    items: tier
      ? [
          {
            item_id: tier,
            item_name: `SuperCalc Pro ${tier}`,
            price: value ?? 0,
            quantity: 1
          }
        ]
      : []
  });
}

/** GA4 purchase — once per Stripe session id. Deduped so thank-you + email unlock do not double-count. */
export function trackPurchaseCompleted({
  transactionId,
  value,
  currency = 'AUD',
  tier,
  billing,
  source = '',
  landingSource = '',
  article = '',
  ctaLocation = '',
  surface = '',
  landingPath = '',
  utmCampaign = '',
  context = 'checkout'
} = {}) {
  if (!transactionId) return false;
  if (!markConversionOnce(PURCHASE_FIRED_PREFIX, transactionId)) return false;

  pushFunnelEvent('purchase', buildPurchasePayload({
    transactionId,
    value,
    currency,
    tier,
    billing,
    source,
    landingSource,
    article,
    ctaLocation,
    surface,
    landingPath,
    utmCampaign,
    context: context || 'checkout'
  }));
  return true;
}

/** Unlock replay (restore access, email link) — not a new sale. */
export function trackSubscriptionRestored({
  transactionId,
  value,
  currency = 'AUD',
  tier,
  billing,
  source = '',
  landingSource = '',
  article = '',
  ctaLocation = '',
  surface = '',
  landingPath = '',
  utmCampaign = ''
} = {}) {
  if (!transactionId) return false;
  if (!markConversionOnce(RESTORE_FIRED_PREFIX, transactionId)) return false;

  pushFunnelEvent('subscription_restored', buildPurchasePayload({
    transactionId,
    value,
    currency,
    tier,
    billing,
    source,
    landingSource,
    article,
    ctaLocation,
    surface,
    landingPath,
    utmCampaign,
    context: 'restore'
  }));
  return true;
}
